import { useEffect, useMemo, useRef, useState } from "react"
import type {
  CarriageLabelOverrideOp,
  CarriageLabelServicesResponse,
  CarriageLabelsResponse,
} from "./carriageLabelsTypes"

// Data hooks for the Carriage Labels tile — plain fetch + useState + useEffect,
// mirroring useMenuCards. credentials:"include" so the SWA auth cookie rides along.

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" })
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { error?: string }
    throw new Error(b.error || `Request failed (${r.status})`)
  }
  return (await r.json()) as T
}

interface Result<T> { data: T | null; loading: boolean; error: string | null }

/** Service picker: distinct (date, time, route) in the date window. */
export function useCarriageLabelServices(from: string, to: string, enabled: boolean): Result<CarriageLabelServicesResponse> {
  const [data, setData] = useState<CarriageLabelServicesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true); setError(null)
    const qs = new URLSearchParams()
    if (from) qs.set("from", from)
    if (to) qs.set("to", to)
    getJson<CarriageLabelServicesResponse>(`/api/dashboards/pullman/carriage-label-services?${qs.toString()}`)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [from, to, enabled])
  return { data, loading, error }
}

/**
 * Save (or clear) label edits. Writes to cache.CarriageLabelOverrides, which is
 * separate from the nightly-truncated cache, so edits persist.
 */
export async function saveCarriageLabelOverrides(ops: CarriageLabelOverrideOp[]): Promise<{ saved: number; cleared: number }> {
  const r = await fetch("/api/dashboards/pullman/carriage-label-overrides", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ops }),
  })
  const body = (await r.json().catch(() => ({}))) as { error?: string; saved?: number; cleared?: number }
  if (!r.ok) throw new Error(body.error || `Save failed (${r.status})`)
  return { saved: Number(body.saved ?? 0), cleared: Number(body.cleared ?? 0) }
}

export interface LabelQuery { date?: string; time?: string; route?: string; from?: string; to?: string; q?: string }

/**
 * The label rows to print. Debounced 300ms (so typing in the search is smooth).
 * `reload()` refetches — used after saving edits so the list shows them.
 */
export function useCarriageLabels(qy: LabelQuery, enabled: boolean): Result<CarriageLabelsResponse> & { reload: () => void } {
  const [data, setData] = useState<CarriageLabelsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)
  const timer = useRef<number | null>(null)

  const url = useMemo(() => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(qy)) if (v) qs.set(k, v)
    return `/api/dashboards/pullman/carriage-labels?${qs.toString()}`
  }, [qy])

  useEffect(() => {
    if (!enabled) { setData(null); return }
    if (timer.current) window.clearTimeout(timer.current)
    let cancelled = false
    timer.current = window.setTimeout(() => {
      setLoading(true); setError(null)
      getJson<CarriageLabelsResponse>(url)
        .then((d) => { if (!cancelled) setData(d) })
        .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 300)
    return () => { cancelled = true; if (timer.current) window.clearTimeout(timer.current) }
  }, [url, enabled, nonce])

  return { data, loading, error, reload: () => setNonce((n) => n + 1) }
}
